// src/oura/components/onboarding/NewClientDialog.tsx
//
// Client signed (JOURNEY-SPEC §2.3): one sheet, one press. Drop in the signed
// quote, say who the contact is and when they paid, and send the kick-off.
// Oura reads the rest: the plan and outlets from the quote, the pitched
// outlets and how customers buy from the scan it already holds, and the
// prospect itself, which onboarding adopts rather than refuses.
//
// Replaces both "New client" and "Start onboarding". The quote is the PDF the
// client signed; the quotation build's engagement.json is read too, for the
// few clients that have one.

import { useEffect, useId, useRef, useState, type DragEvent, type ReactNode } from "react";
import { FileTextIcon, Loader2Icon, UploadIcon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@component/dialog";
import { cn } from "../../lib/cn";
import { FOCUS_RING, Seg, TYPE } from "@oura/ui";
import { listBusinesses } from "@service/aeo";
import { findProspectDraft } from "@service/aeo/scanDraft";
import type { AeoBusiness } from "@service/aeo/types";
import { createClient, getFormLink, getOnboardingPrefill, listOnboardings, type Onboarding, type OnboardingPrefill } from "@service/aeo/onboarding";
import type { BuyMode } from "@service/aeo/scanDraftTypes";
import { isOuraProd } from "../../role";
import { JOURNEY_SAMPLES, journeySample } from "../../newscan/fixtures/journeySamples";
import { parseEngagement, type ParsedSeed } from "../../lib/engagement";
import { readQuotePdf } from "../../lib/quotePdf";
import { QUOTE_PLANS, type QuotePlanKey } from "../../lib/quoteText";
import { callName, dayMonth, dayMonthYear, isOneSetup, nameStop } from "../../onboarding/derive";
import { ONE_SETUP_UI } from "../../onboarding/oneSetup";
import { draftKickoff, isEmail } from "../../onboarding/drafts";
import { cleanDomain, clearFixed, inputClass, readRecipients, toRecipientText, type MessageErrors, type MessageText } from "../../onboarding/messageText";
import { Field, MessageFields, Refusal } from "./fields";
import { OURA_JOINED_SIGNING_ENABLED, SIGN_PRESS } from "../../newscan/flags";

/**
 * "Not paid yet" and "How customers buy" need backend work that is not live
 * yet (the payment route, and `buy_mode` on the onboarding record), so they
 * have a switch of their own besides the New scan one: turn
 * VITE_ENABLE_OURA_JOINED_SIGNING_LIVE on (exactly "true") only once the API
 * accepts and keeps both. Off, or absent: the sheet asks for the paid date, as
 * before, and sends no buy mode.
 */
const JOINED_SIGNING = OURA_JOINED_SIGNING_ENABLED;

/**
 * Signing a website that is already a prospect in Oura adopts that prospect (its
 * business id goes with the create). That needs the backend transition O2 ("signing
 * adopts the prospect"), not deployed yet, so it ships off: exactly "true" turns it
 * on, absent is off. Off, a prospect's website is refused here with the reason and
 * the create never carries its business id, as before this flow (Jack, #4284).
 */
const PROSPECT_ADOPTION = import.meta.env.VITE_ENABLE_OURA_PROSPECT_ADOPTION === "true";

/**
 * A quote to try the flow with, offered everywhere except production.
 *
 * It used to be offered only on FIXTURES (`!isOnboardingLive`), which hid it
 * on staging the moment the onboarding live switch went on — exactly where
 * the staff test runs. A tester following the doc ("Sample data only. Never
 * use a real client") then had no sample to use and had to drop a real
 * client's proposal, which the same doc forbids (#3979).
 *
 * It is parsed in the browser (`parseEngagement`, no PDF read and no upload),
 * so it works the same against the live API: what reaches the server is an
 * ordinary engagement seed named below. Production never shows it — a real
 * customer must not be able to create a fictional client.
 *
 * `page_units` is 0 and `deliverables` is empty ON PURPOSE (#3998): that is the
 * shape of a quote read from a PDF, and it is what tells the server to write
 * the plan's lines from its catalogue. The sample used to say `page_units: 10`
 * with no lines, which the server refuses ("the monthly quantities on G1
 * deliver 0 page units, but the contract sells 10"), so no client could be
 * created from it. Give it page units only together with lines that add up.
 * Made out to the business being signed when there is one, so a walk shows no mismatch line.
 */
/** The sample quotes (fixtures and staging only): one made out to this business, a 2-outlet one, an online-only one. */
type SampleQuoteKind = "match" | "outlets" | "online";
const SAMPLE_QUOTE_LABEL: Record<SampleQuoteKind, string> = { match: "This business", outlets: "2 outlets", online: "Online only" };

/**
 * A sample quote as engagement.json. "This business" is made out to the
 * journey sample on this website (its own number, plan and outlets), else to
 * the business being signed; the others are a 2-outlet and an online-only plan.
 * Its own number per business, as a real quote has, so a second sample signing
 * is not refused as the first one's quote.
 */
function sampleQuote(kind: SampleQuoteKind, client = "Lumi Pilates Studio", domain = ""): { json: string; website: string | null } {
    const j = journeySample(domain) ?? JOURNEY_SAMPLES.find((x) => x.name.toLowerCase() === client.trim().toLowerCase()) ?? null;
    const code = (client.replace(/[^a-z0-9]/gi, "").slice(0, 4) || "LUMI").toUpperCase();
    const base = { page_units: 0, started_at: null, deliverables: [], deviations: [], documents: [] };
    if (kind === "match" && j) {
        return {
            json: JSON.stringify({ ...base, ref: j.quote.ref, client: j.name, plan_name: j.quote.planName, term_months: j.quote.termMonths, outlets: j.quote.outlets }),
            website: j.domain,
        };
    }
    if (kind === "online") {
        return { json: JSON.stringify({ ...base, ref: `OT-2026-${code}-ONL`, client, plan_name: "6-Month plan", term_months: 6, outlets: 0 }), website: null };
    }
    return {
        json: JSON.stringify({ ...base, ref: `OT-2026-${code}-001`, client, plan_name: kind === "match" ? "Annual plan" : "6-Month plan", term_months: kind === "match" ? 12 : 6, outlets: kind === "match" ? 1 : 2 }),
        website: null,
    };
}

const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const sameName = (a: string, b: string) => a.trim().toLowerCase().replace(/[^a-z0-9]+/g, "") === b.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

/** What was read from the quote, whichever file it came from. */
interface Quote {
    fileName: string;
    ref: string;
    client: string;
    /** Null when the quote does not say. */
    outlets: number | null;
    areas: string[];
    /** The plan the file names. Null when the staffer has to say which was signed. */
    plan: QuotePlanKey | null;
    /** What to offer when the plan is asked for. */
    plansOffered: QuotePlanKey[];
    /** The seed a JSON quote carries whole. Null for a PDF, which is built from the facts above. */
    seed: ParsedSeed | null;
    /** The signed PDF itself, kept with the plan. */
    pdf: File | null;
}

function planKeyOf(planName: string, termMonths: number): QuotePlanKey | null {
    if (termMonths === 12 || /annual/i.test(planName)) return "annual";
    if (termMonths === 6 || /6/.test(planName)) return "six";
    return null;
}

async function readQuoteFile(file: File): Promise<Quote> {
    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
    if (isPdf) {
        const q = await readQuotePdf(file);
        if (!q.ref) throw new Error("No quote number in this PDF. Is it the signed quote?");
        const offered: QuotePlanKey[] = q.plansOffered.length > 0 ? q.plansOffered : ["annual", "six", "build"];
        return {
            fileName: file.name, ref: q.ref, client: q.client, outlets: q.outlets, areas: q.areas,
            plan: q.plansOffered.length === 1 ? q.plansOffered[0] : null, plansOffered: offered, seed: null, pdf: file,
        };
    }
    const parsed = parseEngagement(await file.text());
    const s = parsed.seed;
    return {
        fileName: file.name, ref: s.ref, client: s.client, outlets: s.outlets, areas: [],
        plan: planKeyOf(s.planName, s.termMonths), plansOffered: [], seed: s, pdf: null,
    };
}

/**
 * The seed sent to the server. `sourceAssessmentId` is the seed's own value
 * when the file carries one, else the business's latest usable scan: an
 * embedded seed no longer returns early without it (JOURNEY-SPEC §2.3).
 */
function seedOf(q: Quote, plan: QuotePlanKey | null, outlets: number, sourceId: string | null): ParsedSeed {
    const source = q.seed?.sourceAssessmentId || sourceId || undefined;
    if (q.seed) return source ? { ...q.seed, sourceAssessmentId: source } : q.seed;
    const p = QUOTE_PLANS[plan ?? "annual"];
    return {
        ref: q.ref, client: q.client, planName: p.planName, termMonths: p.termMonths, pageUnits: 0,
        outlets: q.outlets ?? Math.max(1, outlets), startedAt: null, deliverables: [], deviations: [], documents: [],
        ...(source ? { sourceAssessmentId: source } : {}),
    };
}

interface ClientErrors { quote?: string; plan?: string; website?: string; name?: string; email?: string; paidOn?: string }

/** The business the sheet signs: given by the caller, or the prospect the website lookup found. */
interface Target {
    /** Null for a prospect that is only a draft or a started scan so far: signing creates the business on its site. */
    businessId: string | null;
    name: string;
    domain: string;
    /** The latest usable scan: the pitch the plan was sold on. */
    scanId: string | null;
    scannedAt: string | null;
    /** Where the prospect stands when it is not scanned yet. Absent: scanned, or not known. */
    stage?: { state: "drafted" | "scanning" | "failed" | "unscanned"; since: string | null };
}

/** With adoption off: why a prospect's website cannot be signed from this sheet yet. */
function prospectBlockedLine(name: string): string {
    return `${name} is already a prospect in Oura. Signing a prospect here is not available yet.`;
}

/** The one line under the website that says which prospect onboarding adopts, in whatever state it is. */
function adoptedLine(t: Target): string {
    const on = (iso: string | null) => (iso ? ` on ${dayMonth(iso)}` : "");
    if (t.scannedAt) return `Already in Oura from a scan on ${dayMonth(t.scannedAt)}: ${nameStop(t.name)} Onboarding uses it.`;
    switch (t.stage?.state) {
        case "scanning": return `Already in Oura, its scan is running: ${nameStop(t.name)} Onboarding uses it and the pitched 50.`;
        case "drafted": return `Already in Oura as a draft${on(t.stage.since)}, not scanned yet: ${nameStop(t.name)} Onboarding uses it.`;
        case "failed": return `Already in Oura, its scan${on(t.stage.since)} failed: ${nameStop(t.name)} Onboarding uses it.`;
        case "unscanned": return `Already in Oura, no finished scan yet: ${nameStop(t.name)} Onboarding uses it.`;
        default: return `Already in Oura: ${nameStop(t.name)} Onboarding uses it.`;
    }
}

const BUY_ITEMS: Array<{ key: BuyMode; label: string }> = [
    { key: "visit", label: "Visit us" },
    { key: "online", label: "Online" },
    { key: "both", label: "Both" },
    { key: "we_go_to_them", label: "We go to them" },
];
/** The buy mode a quote implies: one that sells no outlets is an online plan. No quote: Visit us. */
function quoteBuyMode(q: Quote | null): BuyMode {
    return q?.outlets === 0 ? "online" : "visit";
}

const BUY_LABEL: Record<BuyMode, string> = { visit: "Visit us", online: "Online", both: "Both", we_go_to_them: "We go to them" };

/** A scan that produced a usable score: the pitch a plan can be sold on. */
function usableScan(b: AeoBusiness | undefined): { id: string; at: string } | null {
    const a = b?.latest_assessment;
    if (!a || a.gated || (a.status !== "complete" && a.status !== "partial")) return null;
    return { id: a.assessment_id, at: a.captured_at };
}

/** A business that is a client already: an onboarding record, a plan, or not a prospect. */
/**
 * Already being set up or already served: an onboarding record, or a signed or
 * active plan. Only these refuse signing (Angel, 15 Oct). A "Quoted" business
 * (marked client, a draft engagement or none) signs like a prospect.
 */
const isClient = (b: AeoBusiness | undefined, records: Onboarding[]) =>
    !!b && (b.engagement_status === "signed" || b.engagement_status === "active" || records.some((o) => o.businessId === b.id));

export interface NewClientDialogProps {
    open: boolean;
    onClose: () => void;
    /** The client now exists. `sent` is false when the kick-off was held back. */
    onCreated: (created: { businessId: string; onboarding: Onboarding; sent: boolean }) => void;
    /** Opens a client's onboarding screen. */
    onOpen: (businessId: string) => void;
    /** A business already in Oura that has just signed. Its website is fixed. */
    existing?: { businessId: string; name: string; domain: string };
    /** Where focus goes when the dialog closes: the button that opened it. */
    returnFocus?: () => HTMLElement | null;
}

/* ------------------------------------------------------------------------- *
 * Pieces
 * ------------------------------------------------------------------------- */

/** A label and its value, for the quote card. */
function Fact({ label, children, muted }: { label: string; children: ReactNode; muted?: boolean }) {
    return (
        <div className="min-w-0">
            <dt className={TYPE.lab}>{label}</dt>
            <dd className={cn(TYPE.tb, "m-0 mt-0.5 break-words", muted ? "text-gray-400" : "text-gray-900")}>{children}</dd>
        </div>
    );
}

/** One read-only fact Oura holds, in a sentence. Amber when it needs a look. */
function ReadLine({ label, children, warn }: { label: string; children: ReactNode; warn?: boolean }) {
    return (
        <div>
            <p className={cn(TYPE.lab, "m-0 mb-1.5")}>{label}</p>
            <p className={cn(TYPE.tb, "m-0 rounded-xl px-3 py-2.5", warn ? "bg-amber-50 font-medium text-amber-800" : "bg-gray-50 text-gray-700")}>{children}</p>
        </div>
    );
}

const PRIMARY = cn(
    "inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-blue-600 px-4 text-[13px] font-medium max-sm:h-9 text-white transition-colors hover:bg-blue-700 disabled:opacity-50",
    FOCUS_RING,
);
const SECONDARY = cn(
    "inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-white px-4 text-[13px] font-medium max-sm:h-9 text-gray-900 ring-1 ring-inset ring-black/[0.1] transition-colors hover:bg-gray-50 disabled:opacity-50",
    FOCUS_RING,
);
const QUIET = cn(
    "inline-flex h-8 items-center justify-center rounded-full bg-white px-4 text-[13px] font-medium ring-1 ring-inset ring-black/[0.1] max-sm:h-9 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50",
    FOCUS_RING,
);
const TEXT_BUTTON = cn(
    "inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[13px] font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50 max-sm:h-9",
    FOCUS_RING,
);
const LINK = cn("rounded font-medium text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-800", FOCUS_RING);

function Spin() {
    return <Loader2Icon aria-hidden size={16} className="animate-spin" />;
}

/** "Bangsar, Mont Kiara and Puchong". */
function listOf(names: string[]): string {
    return names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The outlets line (JOURNEY-SPEC §2.3 item 5): what the quote sold against what the pitch asked about. */
function outletsLine(quote: Quote | null, pitched: string[]): { text: string; warn: boolean } | null {
    const sold = quote?.outlets ?? null;
    if (pitched.length > 0) {
        const inPitch = `${pitched.length} in the pitched 50 (${pitched.join(", ")}).`;
        if (sold === null) return { text: `Outlets: ${inPitch}`, warn: false };
        const differ = sold !== pitched.length;
        return {
            text: `Outlets: ${sold} on the quote · ${inPitch}${differ ? " Oura asks about this in the first AI check." : ""}`,
            warn: differ,
        };
    }
    if (!quote) return null;
    // An online plan sells no outlets: nothing for the client to list.
    if (sold === 0) return { text: "Outlets: None on the quote. It sells online.", warn: false };
    if (quote.areas.length > 0) return { text: `Outlets: ${sold ?? quote.areas.length} on the quote (${quote.areas.join(", ")}).`, warn: false };
    if (sold !== null) return { text: `Outlets: ${sold} on the quote. The client lists them on the form.`, warn: false };
    return { text: "Outlets: not on the quote. The client lists them on the form.", warn: false };
}

/* ------------------------------------------------------------------------- *
 * The sheet
 * ------------------------------------------------------------------------- */

export function NewClientDialog({ open, onClose, onCreated, onOpen, existing, returnFocus }: NewClientDialogProps) {
    const [quote, setQuote] = useState<Quote | null>(null);
    const [reading, setReading] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [plan, setPlan] = useState<QuotePlanKey | null>(null);
    const [website, setWebsite] = useState(existing?.domain ?? "");
    /** The prospect the website belongs to, adopted by onboarding. Null until found. */
    const [adopted, setAdopted] = useState<Target | null>(null);
    /** The business the website belongs to that is a client already: refused, with the way there. */
    const [taken, setTaken] = useState<{ businessId: string; name: string } | null>(null);
    const [looking, setLooking] = useState(false);
    /** The website the last finished lookup was for: "not in Oura yet" is said only for that one. */
    const [lookedUp, setLookedUp] = useState<string | null>(null);
    /** A lookup that FAILED: never read as "not in Oura", and nothing is created until it succeeds (Jack, #4284). */
    const [lookupError, setLookupError] = useState<string | null>(null);
    /** The website as typed now, for async work that began with an older value (Jack, #4295). */
    const websiteNow = useRef(existing?.domain ?? "");
    websiteNow.current = website;
    /** What the pitch knows. Empty until read. */
    const [pitch, setPitch] = useState<OnboardingPrefill & { scanId: string | null; scannedAt: string | null }>({ outletAreas: [], scanId: null, scannedAt: null });
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [paidOn, setPaidOn] = useState(today);
    const [notPaid, setNotPaid] = useState(false);
    /**
     * Staff's own pick on "How customers buy", null until they press one. The
     * mode sent is derived, never stored: the pitch's when it has one, else this
     * pick, else what the quote implies. The pick is cleared when the website
     * or the quote changes, so one client's mode never rides into another's
     * record (Jack, #4286).
     */
    const [buyPick, setBuyPick] = useState<BuyMode | null>(null);
    /** The recipient fields are open (staff pressed Change, or a send found a problem in them). */
    const [recipientsOpen, setRecipientsOpen] = useState(false);
    /**
     * What staff typed into To and Copy to themselves. Null: that field follows
     * the contact as typed, so filling the contact email after an empty press
     * still fills To.
     */
    const [toEdit, setToEdit] = useState<string | null>(null);
    const [ccEdit, setCcEdit] = useState<string | null>(null);
    const [errors, setErrors] = useState<ClientErrors>({});
    const [messageErrors, setMessageErrors] = useState<MessageErrors | null>(null);
    const [refused, setRefused] = useState<string | null>(null);
    /** With adoption off: the website looked up belongs to a prospect, which cannot be signed here yet. */
    const [prospectBlocked, setProspectBlocked] = useState<{ domain: string; name: string } | null>(null);
    const [busy, setBusy] = useState<"send" | "hold" | null>(null);
    // The send asks first (#4529): an email cannot be taken back, and Save, send later sends nothing.
    const [confirmingSend, setConfirmingSend] = useState(false);
    const [done, setDone] = useState<{ businessId: string; onboarding: Onboarding; sent: boolean } | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const quoteNoteId = useId();
    const lookup = useRef(0);

    // The last screen takes the focus and starts at the top: the button that led there is gone.
    const first = useRef(true);
    const finished = !!done;
    useEffect(() => {
        if (!open) { first.current = true; return; }
        if (first.current) { first.current = false; return; }
        bodyRef.current?.scrollTo({ top: 0 });
        bodyRef.current?.focus();
    }, [open, finished]);

    // Escape or a click outside keeps what was typed, so an accidental close
    // loses nothing. Cancel and a created client start the next one clean, so
    // a finished client's quote is never 1 click away from a new name.
    const startClean = useRef(true);
    const lastExisting = useRef(existing?.domain);
    useEffect(() => {
        if (!open) return;
        if (lastExisting.current !== existing?.domain) { startClean.current = true; lastExisting.current = existing?.domain; }
        if (!startClean.current) return;
        startClean.current = false;
        setQuote(null); setReading(false); setDragging(false); setPlan(null);
        setWebsite(existing?.domain ?? ""); setAdopted(null); setTaken(null); setProspectBlocked(null); setLooking(false); setLookedUp(null); setLookupError(null);
        setPitch({ outletAreas: [], scanId: null, scannedAt: null });
        setName(""); setEmail(""); setPaidOn(today()); setNotPaid(false); setBuyPick(null); setRecipientsOpen(false); setToEdit(null); setCcEdit(null);
        setErrors({}); setMessageErrors(null); setRefused(null); setBusy(null); setDone(null);
    }, [open, existing?.domain]);

    /** What the pitch ran on: its outlets and buy mode, and the scan it was. */
    const readPitch = async (domain: string, row: AeoBusiness | undefined, current: () => boolean = () => true) => {
        const scan = usableScan(row);
        const prefill = await getOnboardingPrefill(domain);
        // A newer lookup (or an edit of the website) has taken over: this pitch is another site's.
        if (!current()) return;
        setPitch({ ...prefill, scanId: scan?.id ?? null, scannedAt: prefill.pitchedAt ?? scan?.at ?? null });
    };

    // A business that has just signed: read what its pitch knows. Signing waits for the read: a client
    // made before it ends, or after it failed, would not be tied to the scan it was sold on.
    const existingId = existing?.businessId;
    const existingDomain = existing?.domain;
    const [pitchRead, setPitchRead] = useState<"idle" | "loading" | "ready" | "failed">("idle");
    const [pitchRetry, setPitchRetry] = useState(0);
    useEffect(() => {
        if (!open || !existingId || !existingDomain) {
            setPitchRead("idle");
            return;
        }
        let cancelled = false;
        setPitchRead("loading");
        void (async () => {
            try {
                const rows = await listBusinesses();
                if (cancelled) return;
                await readPitch(existingDomain, rows.find((b) => b.id === existingId), () => !cancelled);
                if (!cancelled) setPitchRead("ready");
            } catch {
                if (cancelled) return;
                setPitch({ outletAreas: [], scanId: null, scannedAt: null });
                setPitchRead("failed");
            }
        })();
        return () => { cancelled = true; };
    }, [open, existingId, existingDomain, pitchRetry]);

    /**
     * The website, looked up as it is typed: a prospect is adopted (no 409),
     * a client is refused with the way to it (JOURNEY-SPEC §2.3 item 2).
     */
    const lookUp = async (site: string) => {
        const domain = cleanDomain(site);
        if (!domain || existing) return;
        const mine = ++lookup.current;
        setLooking(true);
        setLookupError(null);
        setLookedUp(null);
        setProspectBlocked(null);
        try {
            // A failed read is a failure, not an empty list: it would say "Not in Oura yet" and create a duplicate.
            const [rows, records] = await Promise.all([listBusinesses(), listOnboardings()]);
            if (lookup.current !== mine) return;
            const row = rows.find((b) => cleanDomain(b.domain) === domain);
            const record = records.find((o) => o.domain === domain);
            if (record || isClient(row, records)) {
                setTaken({ businessId: record?.businessId ?? row!.id, name: record?.businessName ?? row!.business_name });
                setAdopted(null);
                setLookedUp(domain);
                return;
            }
            setTaken(null);
            // A prospect in any state is adopted: scanned, scan failed, scan running, or only drafted (JOURNEY-SPEC §2.3 item 2).
            // Live, a started scan has created the business row already; a draft that never started has none.
            const draftRead = await findProspectDraft(domain);
            // Drafts switched off answer "none"; any other failure fails the lookup.
            if (!draftRead.ok && draftRead.kind !== "absent") throw new Error(draftRead.message);
            const draft = draftRead.ok ? draftRead.data : null;
            if (lookup.current !== mine) return;
            if (!existing && (row || draft) && !PROSPECT_ADOPTION) {
                // Adoption is off (O2 not deployed): refuse rather than promise an adoption the backend cannot do.
                setAdopted(null);
                setProspectBlocked({ domain, name: row?.business_name ?? draft?.businessName ?? domain });
                setLookedUp(domain);
                return;
            }
            if (row) {
                const scan = usableScan(row);
                const latest = row.latest_assessment;
                const stage: Target["stage"] = scan
                    ? undefined
                    : draft?.state === "scanning"
                      ? { state: "scanning", since: draft.since }
                      : latest?.status === "failed"
                        ? { state: "failed", since: latest.captured_at }
                        : { state: "unscanned", since: null };
                setAdopted({ businessId: row.id, name: row.business_name, domain: row.domain, scanId: scan?.id ?? null, scannedAt: scan?.at ?? null, stage });
            } else if (draft) {
                setAdopted({
                    businessId: draft.businessId, name: draft.businessName, domain, scanId: null, scannedAt: null,
                    stage: { state: draft.state, since: draft.since },
                });
            }
            await readPitch(domain, row, () => lookup.current === mine);
            if (lookup.current === mine) {
                setLookedUp(domain);
                fixed("website");
            }
        } catch {
            if (lookup.current !== mine) return;
            setTaken(null);
            setAdopted(null);
            setPitch({ outletAreas: [], scanId: null, scannedAt: null });
            setLookupError("Oura could not check this website. Try again.");
        } finally {
            if (lookup.current === mine) setLooking(false);
        }
    };

    const fixed = (key: keyof ClientErrors) => setErrors((was) => (was[key] ? { ...was, [key]: undefined } : was));

    const takeFile = async (file: File | undefined) => {
        if (!file) return;
        setReading(true);
        setRefused(null);
        fixed("quote");
        try {
            const q = await readQuoteFile(file);
            setQuote(q);
            setPlan(q.plan);
            // A new quote resets the buy line to what it implies, in both directions (a pitch, when read, still wins).
            setBuyPick(null);
            fixed("plan");
            // No website typed yet: a prospect Oura holds under the quote's name fills it, and is then looked up as typed.
            // Read off the field as it is NOW, before and after the list: a website typed while the
            // file was read is the staffer's, and is never overwritten by the quote's match (Jack, #4295).
            if (!existing && !cleanDomain(websiteNow.current) && q.client) {
                const rows = await listBusinesses().catch(() => [] as AeoBusiness[]);
                const named = rows.filter((b) => b.state === "prospect" && sameName(b.business_name, q.client));
                if (named.length === 1 && !cleanDomain(websiteNow.current)) {
                    setWebsite(named[0].domain);
                    setBuyPick(null);
                    fixed("website");
                    void lookUp(named[0].domain);
                }
            }
        } catch (err) {
            setQuote(null);
            const message = err instanceof Error ? err.message : "";
            setErrors((e) => ({ ...e, quote: /quote number|PDF/.test(message) ? message : "Oura could not read this file. Use the signed quote PDF." }));
        } finally {
            setReading(false);
        }
    };
    const onDrop = (e: DragEvent<HTMLElement>) => {
        e.preventDefault();
        setDragging(false);
        void takeFile(e.dataTransfer.files?.[0]);
    };

    const target: Target | null = existing
        ? { ...existing, scanId: pitch.scanId, scannedAt: pitch.scannedAt }
        : adopted;
    const pitched = pitch.outletAreas;
    // Never typed: the pitched outlets, else the quote's named areas.
    const outletAreas = pitched.length > 0 ? pitched : quote?.areas ?? [];
    const outlets = outletsLine(quote, pitched);
    const fromPitch = !!pitch.buyMode;
    const buy: BuyMode = pitch.buyMode ?? buyPick ?? quoteBuyMode(quote);
    const contact = { name: name.trim(), email: email.trim() };
    const fromContact = toRecipientText(draftKickoff({ contact }));
    const kickoffText: MessageText = { ...fromContact, to: toEdit ?? fromContact.to, cc: ccEdit ?? fromContact.cc };
    // A To that follows the contact is fixed the moment the contact email is: its old error goes with it.
    const toFollowsFixed = !!messageErrors?.to && toEdit === null && !!fromContact.to.trim();
    const recipientErrors: MessageErrors | null = toFollowsFixed
        ? messageErrors?.cc ? { cc: messageErrors.cc } : null
        : messageErrors;

    /**
     * A refused submit always shows why where the eye is: the first error, or
     * the refusal at the top, scrolled into view and focused. On a phone the
     * "quote already used" line sat off-screen and the button seemed dead (Angel, 15 Oct).
     */
    const showFirstError = () =>
        requestAnimationFrame(() =>
            requestAnimationFrame(() => {
                const body = bodyRef.current;
                const el =
                    body?.querySelector<HTMLElement>("[data-refusal]") ??
                    body?.querySelector<HTMLElement>('[data-new-client] [aria-invalid="true"], [data-new-client] [role="alert"]');
                if (!el) return;
                el.scrollIntoView({ block: "center", behavior: "auto" });
                if (el.tabIndex < 0 && !el.matches("input, button, textarea, select")) el.setAttribute("tabindex", "-1");
                el.focus({ preventScroll: true });
            }),
        );

    const create = async (send: boolean) => {
        const domain = cleanDomain(website);
        const next: ClientErrors = {};
        if (!quote) next.quote = errors.quote ?? "Add the signed quote.";
        else if (!plan && !quote.seed) next.plan = "Pick the plan they signed.";
        if (!domain) next.website = website.trim() ? "Check the website, like clinic.com." : "Add the website, like clinic.com.";
        if (!contact.name) next.name = "Add a name.";
        if (!contact.email) next.email = "Add an email.";
        else if (!isEmail(contact.email)) next.email = "Check this address.";
        if (!notPaid) {
            if (!paidOn) next.paidOn = "Add the date.";
            else if (paidOn > today()) next.paidOn = "That date has not happened yet.";
        }
        // The kick-off goes only when paid (call 18).
        const sending = send && !notPaid;
        const read = sending ? readRecipients(kickoffText) : null;
        setErrors(next);
        setMessageErrors(read?.errors ?? null);
        if (read?.errors) setRecipientsOpen(true);
        if (Object.keys(next).length > 0 || !quote || taken || (read && !read.draft)) {
            // To the first thing that needs fixing, so the error is read where it is.
            showFirstError();
            return;
        }
        // A prospect's website is not signed here while adoption is off (O2, Jack #4284).
        if (!existing && prospectBlocked?.domain === domain) {
            setErrors((was) => ({ ...was, website: prospectBlockedLine(prospectBlocked.name) }));
            showFirstError();
            return;
        }
        // A new site is created only after a lookup of THIS site succeeded (Jack, #4284).
        if (!existing && (looking || lookupError || lookedUp !== domain)) {
            setErrors((was) => ({ ...was, website: lookupError ?? "Wait for Oura to finish checking this website." }));
            if (!looking && !lookupError) void lookUp(domain);
            showFirstError();
            return;
        }

        // A business already in Oura is signed only once its pitch has been read.
        if (existing && pitchRead !== "ready") {
            if (pitchRead === "failed") {
                setRefused("The pitch could not be read, so the plan cannot be tied to its scan yet. Try again.");
                setPitchRetry((n) => n + 1);
            } else {
                setRefused("Wait for Oura to finish reading the pitch, then try again.");
            }
            showFirstError();
            return;
        }

        bodyRef.current?.focus();
        setBusy(sending ? "send" : "hold");
        setRefused(null);
        try {
            const others = await listOnboardings();
            const sameSite = others.find((o) => o.domain === domain || o.businessId === target?.businessId);
            const sameQuote = others.find((o) => o.plan?.ref === quote.ref);
            if (sameSite) {
                if (existing) setRefused(`${sameSite.businessName} is already being set up.`);
                setTaken({ businessId: sameSite.businessId, name: sameSite.businessName });
                setBusy(null);
                showFirstError();
                return;
            }
            if (sameQuote) {
                setErrors({ quote: `Quote ${quote.ref} is already on ${sameQuote.businessName}.` });
                setBusy(null);
                showFirstError();
                return;
            }
            // The link is not shown (the email is a template, #4155), but a
            // form link that cannot be made still stops here, as it always has.
            await getFormLink(domain, outletAreas);
        } catch {
            setRefused("The form link could not be made. Try again in a moment.");
            setBusy(null);
            showFirstError();
            return;
        }
        // A request that throws (a timeout, no network) still frees the dialog: busy used to stay
        // set, and a busy dialog can neither act nor close (Jack, #4284).
        try {
            const result = await createClient({
                engagement: seedOf(quote, plan, outletAreas.length, target?.scanId ?? null),
                quotePdf: quote.pdf,
                domain,
                contact,
                outletAreas,
                paidOn: notPaid ? null : paidOn,
                ...(JOINED_SIGNING ? { buyMode: buy } : {}),
                // A prospect's id goes only with adoption on; a business already signed is always sent.
                businessId: existing?.businessId ?? (PROSPECT_ADOPTION ? target?.businessId ?? undefined : undefined),
                kickoff: read?.draft ?? null,
            });
            // The reasons, not only the count: "1 problem(s)" told nobody what to fix (#3998).
            if (!result.ok) {
                // A quote the server says is used is the quote's own error, at the quote; anything else at the top.
                if (result.code === "quote_taken") setErrors((e) => ({ ...e, quote: result.message }));
                else setRefused([result.message, ...(result.reasons ?? [])].join(" "));
                showFirstError();
                return;
            }
            const created = { businessId: result.businessId, onboarding: result.onboarding, sent: sending };
            setDone(created);
            startClean.current = true;
            onCreated(created);
        } catch {
            setRefused("The client could not be created. Check your connection and try again.");
            showFirstError();
        } finally {
            setBusy(null);
        }
    };

    // A quote made out to another name is worth a second look before it is filed under this one.
    const otherName = target && quote?.client && !sameName(quote.client, target.name) ? quote.client : null;
    // No business on this website: the new one takes the quote's name, so say it before it is created.
    const typedDomain = cleanDomain(website);
    const newBusiness =
        !existing && !adopted && !taken && !prospectBlocked && !looking && lookedUp === typedDomain && typedDomain && quote?.client
            ? { name: quote.client, domain: typedDomain }
            : null;
    const doneName = done ? existing?.name ?? done.onboarding.businessName : "";
    const title = done ? `${doneName} is a client` : SIGN_PRESS;
    // One setup: the first email is the Setup email (a sign-in and a link to
    // Setup), not the kick-off. The record says which once it exists; before
    // that, the flag the Setup page itself runs on.
    const setupEmail = done ? isOneSetup(done.onboarding) : ONE_SETUP_UI;
    const firstEmail = setupEmail ? "Setup email" : "kick-off";
    const sub = done
        ? done.sent ? `${setupEmail ? "Setup email" : "Kick-off"} sent to ${callName(done.onboarding.contact.name)}.`
            : !done.onboarding.paidAt ? `The ${firstEmail} goes once the payment is recorded.`
            : `The ${setupEmail ? "Setup" : "kick-off"} email is not sent yet.`
        : existing ? `${nameStop(existing.name)} Oura sets up the rest.`
        // "Not paid yet" is a choice only with joined signing on; then the line never says paid.
        : JOINED_SIGNING && notPaid ? "Signed. Oura sets up the rest." : "Signed and paid. Oura sets up the rest.";
    const kickoffTo = kickoffText.to.split(/[,;\n]/).map((t) => t.trim()).filter(Boolean);
    const kickoffCc = kickoffText.cc.split(/[,;\n]/).map((t) => t.trim()).filter(Boolean);
    const kickoffStatus = notPaid
        ? `The ${firstEmail} is held until the payment is recorded.`
        : `Sends the ${firstEmail} to ${toEdit !== null ? listOf(kickoffTo) || "nobody yet" : contact.name || "the contact"}${kickoffCc.length > 0 ? `, copy ${listOf(kickoffCc)}` : ""}, as Angel.`;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
            <DialogContent
                className="flex max-h-[90vh] flex-col gap-0 overflow-hidden border-0 p-0 sm:max-w-[600px] sm:rounded-2xl"
                closeButtonClassName="right-5 top-5 grid h-9 w-9 place-items-center rounded-full opacity-100 text-gray-500 hover:bg-gray-100 hover:text-gray-900 data-[state=open]:bg-transparent"
                onOpenAutoFocus={(e) => {
                    const target = document.querySelector<HTMLElement>("[data-quote-choose]");
                    if (target) { e.preventDefault(); target.focus(); }
                }}
                onCloseAutoFocus={(e) => {
                    const target = returnFocus?.();
                    if (target) { e.preventDefault(); target.focus(); }
                }}
            >
                {/* Header */}
                <div className="shrink-0 px-6 pb-5 pt-6">
                    <DialogTitle className="pr-10 text-[22px] font-semibold leading-7 tracking-[-0.01em] text-gray-900">{title}</DialogTitle>
                    <DialogDescription className={cn(TYPE.tb, "mt-1 text-gray-500")}>{sub}</DialogDescription>
                </div>

                {/* Body */}
                <div ref={bodyRef} tabIndex={-1} className="min-h-0 flex-1 overflow-y-auto border-t border-gray-100 px-6 py-5 outline-none">
                    {refused && <div data-refusal="" tabIndex={-1} className="mb-4 rounded-xl outline-none"><Refusal>{refused}</Refusal></div>}

                    {done && (
                        <div className="space-y-2">
                            <p className={cn(TYPE.tb, "m-0 text-gray-700")}>
                                {/* The next step is ours until the kick-off is out; only then is it the client's. */}
                                {done.sent ? (
                                    <>
                                        {callName(done.onboarding.contact.name)} {setupEmail ? "answers the questions on their Setup page" : "answers the form"}. Oura then drafts the first AI check from{" "}
                                        {/* One rule with the Review and the Overview card: only a pitch drafted in Oura carries its 50. */}
                                        {fromPitch ? "the pitched 50" : "the site and the answers"} and tells you when it is ready to review.
                                    </>
                                ) : done.onboarding.paidAt ? (
                                    `Send the ${firstEmail} from the onboarding page when you are ready.`
                                ) : (
                                    "Record the payment on the onboarding page when it clears."
                                )}
                            </p>
                        </div>
                    )}

                    {!done && (
                        <div data-new-client="" className="space-y-5">
                            {/* 1. The signed quote */}
                            <section aria-labelledby={`${quoteNoteId}-h`}>
                                <div className="mb-1.5 flex items-baseline justify-between gap-3">
                                    <p id={`${quoteNoteId}-h`} className={cn(TYPE.lab, "m-0")}>Signed quote</p>
                                    {!isOuraProd() && !quote && !reading && (
                                        <span className={cn(TYPE.tc, "flex flex-wrap items-baseline justify-end gap-x-2 text-gray-500")}>
                                            Sample quote:
                                            {(Object.keys(SAMPLE_QUOTE_LABEL) as SampleQuoteKind[]).map((kind) => (
                                                <button
                                                    key={kind}
                                                    type="button"
                                                    onClick={() => {
                                                        const domain = existing?.domain ?? adopted?.domain ?? cleanDomain(website);
                                                        // A new site with no business yet: the quote is made out to a name from its website.
                                                        const fromSite = domain ? domain.split(".")[0].replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : undefined;
                                                        const q = sampleQuote(kind, existing?.name ?? adopted?.name ?? fromSite, domain);
                                                        // Made out to a journey sample: its website comes with it when none is typed.
                                                        if (!existing && !adopted && !cleanDomain(website) && q.website) {
                                                            setWebsite(q.website);
                                                            setBuyPick(null);
                                                            fixed("website");
                                                            void lookUp(q.website);
                                                        }
                                                        void takeFile(new File([q.json], `Sample quote, ${SAMPLE_QUOTE_LABEL[kind].toLowerCase()}`, { type: "application/json" }));
                                                    }}
                                                    className={cn("rounded underline decoration-gray-300 underline-offset-2 hover:text-gray-900", FOCUS_RING)}
                                                >
                                                    {SAMPLE_QUOTE_LABEL[kind]}
                                                </button>
                                            ))}
                                        </span>
                                    )}
                                </div>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept=".pdf,application/pdf,.json,application/json"
                                    className="sr-only"
                                    tabIndex={-1}
                                    aria-hidden
                                    onChange={(e) => { void takeFile(e.target.files?.[0]); e.target.value = ""; }}
                                />
                                {!quote ? (
                                    <button
                                        type="button"
                                        data-quote-choose=""
                                        aria-describedby={quoteNoteId}
                                        aria-invalid={!!errors.quote}
                                        onClick={() => fileRef.current?.click()}
                                        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                                        onDragLeave={() => setDragging(false)}
                                        onDrop={onDrop}
                                        disabled={reading}
                                        className={cn(
                                            "flex h-12 w-full items-center gap-3 rounded-xl border border-dashed px-3.5 text-left transition-colors",
                                            dragging ? "border-blue-500 bg-blue-50/60" : errors.quote ? "border-rose-300 bg-rose-50/40" : "border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50",
                                            FOCUS_RING,
                                        )}
                                    >
                                        <span className="shrink-0 text-blue-600">{reading ? <Spin /> : <UploadIcon aria-hidden size={16} />}</span>
                                        <span className={cn(TYPE.tb, "min-w-0 flex-1 truncate text-gray-500")}>
                                            {reading ? "Reading the quote" : "Drop the signed quote PDF"}
                                        </span>
                                    </button>
                                ) : (
                                    <div className="rounded-xl bg-white p-4 ring-1 ring-black/[0.08]">
                                        <div className="flex items-center gap-3">
                                            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-gray-100 text-gray-600">
                                                <FileTextIcon aria-hidden size={16} />
                                            </span>
                                            <p className={cn(TYPE.tb, "m-0 min-w-0 flex-1 truncate font-medium text-gray-900")} title={quote.fileName}>{quote.fileName}</p>
                                            <button type="button" data-quote-choose="" onClick={() => fileRef.current?.click()} className={cn(QUIET, "h-8 text-[13px] max-sm:h-9")}>
                                                Change
                                            </button>
                                        </div>
                                        <dl className="m-0 mt-3.5 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-gray-100 pt-3.5">
                                            {/* Who the quote is made out to: a quote for another business is seen before it is filed. */}
                                            <div className="col-span-2">
                                                <Fact label="Client on the quote" muted={!quote.client}>{quote.client || "Not on the quote"}</Fact>
                                            </div>
                                            <Fact label="Quote">{quote.ref}</Fact>
                                            <Fact label="Plan" muted={!plan}>{plan ? QUOTE_PLANS[plan].label : "Pick below"}</Fact>
                                            <Fact label="Outlets" muted={quote.outlets === null}>{quote.outlets === 0 ? "None, sells online" : quote.outlets ?? "Not in the quote"}</Fact>
                                            <Fact label="Starts">{quote.seed?.startedAt ? dayMonthYear(quote.seed.startedAt) : "On the payment date"}</Fact>
                                        </dl>
                                        {!quote.seed && quote.plansOffered.length > 1 && (
                                            <div className="mt-3.5 border-t border-gray-100 pt-3.5">
                                                <p className={cn(TYPE.lab, "m-0 mb-1.5", errors.plan && "text-rose-600")}>
                                                    {errors.plan ?? "Which plan did they sign?"}
                                                </p>
                                                <div role="radiogroup" aria-label="Which plan did they sign?" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                                    {quote.plansOffered.map((k) => {
                                                        const on = plan === k;
                                                        return (
                                                            <button
                                                                key={k}
                                                                type="button"
                                                                role="radio"
                                                                aria-checked={on}
                                                                onClick={() => { setPlan(k); fixed("plan"); }}
                                                                className={cn(
                                                                    TYPE.tb,
                                                                    "flex h-11 items-center gap-2.5 rounded-xl px-3.5 text-left ring-1 ring-inset transition-colors",
                                                                    on ? "bg-blue-50 font-medium text-blue-700 ring-2 ring-blue-600" : "bg-white text-gray-900 ring-black/[0.1] hover:bg-gray-50",
                                                                    FOCUS_RING,
                                                                )}
                                                            >
                                                                <span aria-hidden className={cn("grid h-4 w-4 shrink-0 place-items-center rounded-full border-2", on ? "border-blue-600" : "border-gray-300")}>
                                                                    {on && <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />}
                                                                </span>
                                                                {QUOTE_PLANS[k].label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {otherName && (
                                            <p role="status" className={cn(TYPE.tc, "m-0 mt-3.5 rounded-lg bg-amber-50 px-3 py-2 font-medium text-amber-800")}>
                                                This quote is for {otherName}. You are onboarding {target?.name}.
                                            </p>
                                        )}
                                    </div>
                                )}
                                {(errors.quote || !quote) && (
                                    <p id={quoteNoteId} role={errors.quote ? "alert" : undefined} className={cn(TYPE.tc, "m-0 mt-1.5", errors.quote ? "font-medium text-rose-600" : "text-gray-500")}>
                                        {errors.quote ?? "Oura reads the quote number, plan and outlets."}
                                    </p>
                                )}
                            </section>

                            {/* 2. The website, only for a business Oura does not hold yet */}
                            {!existing && (
                                <section className="border-t border-gray-100 pt-5">
                                    <Field label="Website" error={errors.website}>
                                        {({ id, describedBy }) => (
                                            <input
                                                id={id}
                                                aria-describedby={describedBy}
                                                aria-invalid={!!errors.website || !!taken}
                                                className={inputClass(errors.website)}
                                                value={website}
                                                onChange={(e) => {
                                                    // A lookup still on its way is for the old site: it must not adopt or prefill.
                                                    lookup.current += 1;
                                                    setLooking(false);
                                                    setLookupError(null);
                                                    // Any edit is looked up again before create, also one that cleans to the
                                                    // same domain ("https://example.com"): the cached answer does not stand in (Jack, #4289).
                                                    setLookedUp(null);
                                                    setWebsite(e.target.value); fixed("website"); setTaken(null); setAdopted(null); setProspectBlocked(null); setPitch({ outletAreas: [], scanId: null, scannedAt: null });
                                                    setBuyPick(null);
                                                }}
                                                onBlur={() => void lookUp(website)}
                                                placeholder="clinic.com"
                                                data-website-field=""
                                                disabled={!!adopted}
                                                autoComplete="off"
                                            />
                                        )}
                                    </Field>
                                    {looking && <p className={cn(TYPE.tc, "m-0 mt-1.5 text-gray-500")}>Looking up the site</p>}
                                    {lookupError && !looking && (
                                        <p role="alert" className={cn(TYPE.tc, "m-0 mt-1.5 font-medium text-rose-600")}>
                                            {lookupError}{" "}
                                            <button type="button" className="underline" onClick={() => void lookUp(website)}>
                                                Try again
                                            </button>
                                        </p>
                                    )}
                                    {prospectBlocked && !looking && prospectBlocked.domain === typedDomain && (
                                        <p role="alert" className={cn(TYPE.tc, "m-0 mt-1.5 font-medium text-rose-600")}>
                                            {prospectBlockedLine(prospectBlocked.name)}
                                        </p>
                                    )}
                                    {newBusiness && (
                                        <p className={cn(TYPE.tc, "m-0 mt-1.5 text-gray-500")}>
                                            Not in Oura yet. Oura adds {newBusiness.name} at {newBusiness.domain}, the name on the quote.
                                        </p>
                                    )}
                                    {adopted && (
                                        <p className={cn(TYPE.tc, "m-0 mt-1.5 text-gray-500")}>
                                            {adoptedLine(adopted)}{" "}
                                            <button
                                                type="button"
                                                className={LINK}
                                                onClick={() => {
                                                    setAdopted(null);
                                                    setPitch({ outletAreas: [], scanId: null, scannedAt: null });
                                                    setBuyPick(null);
                                                    // The field is where the change is made: it takes the focus once it is enabled again.
                                                    requestAnimationFrame(() => {
                                                        const el = document.querySelector<HTMLInputElement>("[data-website-field]");
                                                        el?.focus();
                                                        el?.select();
                                                    });
                                                }}
                                            >
                                                Change
                                            </button>
                                        </p>
                                    )}
                                    {taken && (
                                        <p role="alert" className={cn(TYPE.tc, "m-0 mt-1.5 font-medium text-rose-600")}>
                                            {taken.name} is already a client. Open it instead.{" "}
                                            <button type="button" className={LINK} onClick={() => { onOpen(taken.businessId); onClose(); }}>Open {taken.name}</button>
                                        </p>
                                    )}
                                </section>
                            )}

                            {/* 3. The contact, and 4. when they paid */}
                            <section className="border-t border-gray-100 pt-5">
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <Field label="Contact name" error={errors.name}>
                                        {({ id, describedBy }) => (
                                            <input id={id} aria-describedby={describedBy} aria-invalid={!!errors.name} className={inputClass(errors.name)} value={name} onChange={(e) => { setName(e.target.value); fixed("name"); }} placeholder="Sarah Lim" autoComplete="off" />
                                        )}
                                    </Field>
                                    <Field label="Contact email" error={errors.email}>
                                        {({ id, describedBy }) => (
                                            <input id={id} type="email" aria-describedby={describedBy} aria-invalid={!!errors.email} className={inputClass(errors.email)} value={email} onChange={(e) => { setEmail(e.target.value); fixed("email"); }} placeholder="sarah@clinic.com" autoComplete="off" />
                                        )}
                                    </Field>
                                    {notPaid ? (
                                        <div className="sm:col-span-2">
                                            <p className={cn(TYPE.lab, "m-0 mb-1.5")}>Paid on</p>
                                            <p className={cn(TYPE.tb, "m-0 flex flex-wrap items-center gap-x-2 text-gray-700")}>
                                                {setupEmail ? "Not paid yet. Nothing goes to the client until the payment is recorded." : "Not paid yet. The plan starts on the payment date."}
                                                <button type="button" className={LINK} onClick={() => setNotPaid(false)}>Paid already</button>
                                            </p>
                                        </div>
                                    ) : (
                                        <div>
                                            <Field label="Paid on" error={errors.paidOn}>
                                                {({ id, describedBy }) => (
                                                    <input id={id} type="date" max={today()} aria-describedby={describedBy} aria-invalid={!!errors.paidOn} className={inputClass(errors.paidOn)} value={paidOn} onChange={(e) => { setPaidOn(e.target.value); fixed("paidOn"); }} />
                                                )}
                                            </Field>
                                            {JOINED_SIGNING && (
                                                <button type="button" className={cn(TYPE.tc, "mt-1.5", LINK)} onClick={() => { setNotPaid(true); fixed("paidOn"); }}>Not paid yet</button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* 5. Outlets and 5b. how customers buy: read, not typed */}
                            {(outlets || target || quote) && (
                                <section className="space-y-4 border-t border-gray-100 pt-5">
                                    {outlets && <ReadLine label="Outlets" warn={outlets.warn}>{outlets.text.replace(/^Outlets: /, "")}</ReadLine>}
                                    {!JOINED_SIGNING ? null : fromPitch && pitch.buyMode ? (
                                        <ReadLine label="How customers buy">
                                            {pitch.scannedAt ? `Sells: ${BUY_LABEL[pitch.buyMode]}, from the pitch on ${dayMonth(pitch.scannedAt)}.` : `Sells: ${BUY_LABEL[pitch.buyMode]}, from the pitch.`}
                                        </ReadLine>
                                    ) : (
                                        <div>
                                            <p className={cn(TYPE.lab, "m-0 mb-1.5")}>How customers buy</p>
                                            <Seg items={BUY_ITEMS} value={buy} onChange={setBuyPick} ariaLabel="How customers buy" />
                                            {/* An online client has no outlets: a quote that sells some is worth a look before it is filed. */}
                                            {buy === "online" && (quote?.outlets ?? 0) > 0 && (
                                                <p role="status" className={cn(TYPE.tc, "m-0 mt-2 rounded-lg bg-amber-50 px-3 py-2 font-medium text-amber-800")}>
                                                    The quote sells {quote?.outlets} {quote?.outlets === 1 ? "outlet" : "outlets"}. Online clients have none, so the plan reads online. Check the quote.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </section>
                            )}

                            {/* 6. The kick-off: a status line, with the recipients behind Change */}
                            <section className="border-t border-gray-100 pt-5">
                                <p className={cn(TYPE.tc, "m-0 text-gray-600")}>
                                    {kickoffStatus}
                                    {!notPaid && !recipientsOpen && (
                                        <>
                                            {" "}
                                            <button type="button" className={LINK} onClick={() => setRecipientsOpen(true)}>Change</button>
                                        </>
                                    )}
                                </p>
                                {!notPaid && recipientsOpen && (
                                    <div className="mt-3">
                                        <MessageFields
                                            value={kickoffText}
                                            onChange={(next) => {
                                                setMessageErrors((was) => clearFixed(was, kickoffText, next));
                                                setRefused(null);
                                                if (next.to !== kickoffText.to) setToEdit(next.to);
                                                if (next.cc !== kickoffText.cc) setCcEdit(next.cc);
                                            }}
                                            errors={recipientErrors}
                                            disabled={!!busy}
                                            recipientsOnly
                                        />
                                    </div>
                                )}
                            </section>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-white px-6 py-3.5 sm:flex-row sm:items-center [&>button]:max-sm:w-full">
                    {done ? (
                        <>
                            <button type="button" className={cn(SECONDARY, "ml-auto")} onClick={onClose}>Close</button>
                            <button type="button" className={PRIMARY} onClick={() => { onOpen(done.businessId); onClose(); }}>Open onboarding</button>
                        </>
                    ) : (
                        <>
                            {/* Cancel on every width, as every Oura sheet has it: on a phone the X alone was easy to miss (Angel, 15 Oct). */}
                            <button type="button" className={TEXT_BUTTON} onClick={() => { startClean.current = true; onClose(); }} disabled={!!busy}>Cancel</button>
                            {!notPaid && (
                                <button type="button" className={cn(TEXT_BUTTON, "sm:ml-auto")} disabled={!!busy || reading} onClick={() => void create(false)}>
                                    {busy === "hold" && <Spin />}Save, send later
                                </button>
                            )}
                            <button type="button" className={cn(PRIMARY, notPaid && "sm:ml-auto")} disabled={!!busy || reading || looking} onClick={() => (notPaid ? void create(true) : setConfirmingSend(true))}>
                                {busy && (busy === "send" || notPaid) && <Spin />}
                                {notPaid ? "Save, send when paid" : `Start onboarding and send the ${firstEmail}`}
                            </button>
                        </>
                    )}
                </div>
                <Dialog open={confirmingSend} onOpenChange={(o) => { if (!o) setConfirmingSend(false); }}>
                    <DialogContent className="gap-0 p-6 sm:max-w-[460px] sm:rounded-2xl">
                        <DialogTitle className="pr-8 text-lg font-semibold leading-snug text-gray-900">Send the {firstEmail} now?</DialogTitle>
                        <DialogDescription asChild>
                            <div className={cn(TYPE.tb, "mt-2 space-y-2 text-gray-600")}>
                                <p className="m-0">{kickoffStatus}</p>
                                <p className="m-0 text-gray-500">An email cannot be taken back. To set up the client without emailing anyone, use Save, send later.</p>
                            </div>
                        </DialogDescription>
                        <div className="mt-5 flex justify-end gap-2">
                            <button type="button" className={TEXT_BUTTON} onClick={() => setConfirmingSend(false)}>Cancel</button>
                            <button type="button" className={PRIMARY} onClick={() => { setConfirmingSend(false); void create(true); }}>Start and send</button>
                        </div>
                    </DialogContent>
                </Dialog>
            </DialogContent>
        </Dialog>
    );
}
